import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useAxios } from '@/hooks/useAxios';
import { cn } from '@/utils/classname';

import {
  DEFAULT_GOAL,
  invalidateOsintQueries,
  Pipeline,
} from './osintConstantineConstants';
import { PipelineSelect } from './PipelineSelect';

interface UrlTabContentProps {
  onClose: () => void;
}

const inputClasses = cn(
  'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm',
  'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-hidden',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

export function UrlTabContent({ onClose }: UrlTabContentProps) {
  const axios = useAxios();

  const [repoUrl, setRepoUrl] = useState('');
  const [goal, setGoal] = useState('');
  const [pipeline, setPipeline] = useState<Pipeline>('premium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async () => {
    if (!repoUrl.trim()) {
      setError('Repository URL is required');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await axios.post('/osint/submit', {
        repo_url: repoUrl.trim(),
        goal: goal.trim() || DEFAULT_GOAL,
        pipeline,
      });

      invalidateOsintQueries();

      setSuccess(
        'Constantine scan initiated. Results will appear in the OSINT tab.'
      );
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message =
        axiosErr?.response?.data?.message ||
        'Failed to submit scan. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const busy = isSubmitting || !!success;

  return (
    <div className="space-y-4">
      {error && (
        <div
          className="bg-chariot-status-critical/10 text-chariot-status-critical rounded-md p-2 text-sm"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="bg-chariot-status-positive/10 text-chariot-status-positive rounded-md p-2 text-sm"
          role="status"
        >
          {success}
        </div>
      )}

      {/* Repository URL */}
      <div>
        <label
          htmlFor="osint-url-repo-url"
          className="text-chariot-text-primary mb-1 block text-sm font-medium"
        >
          Repository URL
        </label>
        <input
          id="osint-url-repo-url"
          type="url"
          value={repoUrl}
          onChange={e => {
            setRepoUrl(e.target.value);
            if (error) setError('');
          }}
          placeholder="https://github.com/org/repo"
          required
          disabled={busy}
          className={inputClasses}
        />
      </div>

      {/* Run Tier */}
      <PipelineSelect
        value={pipeline}
        onValueChange={setPipeline}
        disabled={busy}
      />

      {/* Research Goal */}
      <div>
        <label
          htmlFor="osint-url-goal"
          className="text-chariot-text-primary mb-1 block text-sm font-medium"
        >
          Research Goal
        </label>
        <textarea
          id="osint-url-goal"
          rows={8}
          value={goal}
          onChange={e => setGoal(e.target.value)}
          placeholder={DEFAULT_GOAL}
          disabled={busy}
          className={cn(inputClasses, 'resize-y')}
        />
        <div className="mt-1 flex flex-col gap-1">
          {!goal.trim() && (
            <button
              type="button"
              onClick={() => setGoal(DEFAULT_GOAL)}
              className="text-chariot-layer-primary self-start text-xs hover:underline"
            >
              Use default prompt
            </button>
          )}
          <p className="text-chariot-text-secondary text-xs">
            {!goal.trim()
              ? 'Describe what vulnerabilities to hunt for, or use the default prompt above.'
              : goal === DEFAULT_GOAL
                ? 'Default prompt targets critical 0-day vulnerabilities.'
                : 'Your custom goal will focus the analysis.'}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between pt-2">
        <div />
        <div className="flex gap-2">
          <Button
            onClick={onClose}
            variant="ghost"
            className="bg-chariot-background-primary m-0! px-4"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!repoUrl.trim() || busy}
            className="ml-2 min-w-24 px-4"
          >
            {isSubmitting ? 'Submitting...' : 'Start Scan'}
          </Button>
        </div>
      </div>
    </div>
  );
}
